import { useCallback, useRef } from 'react';

import { TooltipData } from './CursorTooltip.types';
import { processTooltipElement } from './processTooltipElement';
import {
  findTooltipElementAtPosition,
  isInsideContainer,
} from './tooltipHelpers';
import { usePositionUpdate } from './usePositionUpdate';
import { useTooltipActions } from './useTooltipActions';

interface UseTooltipStateParams {
  containerRef: React.RefObject<HTMLDivElement>;
  tooltipRef: React.RefObject<HTMLDivElement>;
  tooltipElementsRef: React.MutableRefObject<HTMLElement[]>;
  onVisibilityChange?: (isVisible: boolean) => void;
  onActiveTooltipChange?: (tooltipId: string | null) => void;
  setIsVisible: (visible: boolean) => void;
  setTooltipData: React.Dispatch<React.SetStateAction<TooltipData>>;
  isVisible: boolean;
}

const maxWidth = 320;
const offset = 15;
const viewportPadding = 10;

export const useTooltipState = (params: UseTooltipStateParams) => {
  const isUpdatingRef = useRef(false);

  const { positionRef, updatePosition } = usePositionUpdate(
    params.tooltipRef,
    maxWidth,
    offset,
    viewportPadding,
  );

  const { hideTooltip, showTooltip } = useTooltipActions({
    isVisible: params.isVisible,
    setIsVisible: params.setIsVisible,
    setTooltipData: params.setTooltipData,
    onVisibilityChange: params.onVisibilityChange,
    onActiveTooltipChange: params.onActiveTooltipChange,
  });

  const updateTooltipState = useCallback(
    (clientX: number, clientY: number) => {
      if (!params.containerRef.current || isUpdatingRef.current) return;
      isUpdatingRef.current = true;

      const insideContainer = isInsideContainer(
        clientX,
        clientY,
        params.containerRef,
      );

      if (!insideContainer) {
        hideTooltip();
        isUpdatingRef.current = false;
        return;
      }

      const element = findTooltipElementAtPosition(
        clientX,
        clientY,
        params.tooltipElementsRef.current,
      );

      if (element) {
        processTooltipElement(element, clientX, clientY, {
          updatePosition,
          showTooltip,
          hideTooltip,
        });
      } else {
        hideTooltip();
      }

      isUpdatingRef.current = false;
    },
    [params, hideTooltip, updatePosition, showTooltip],
  );

  return { updateTooltipState, positionRef };
};
